import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

const ExpenseReportStatusSection = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userStatuses, setUserStatuses] = useState([]);
  const [displayInfo, setDisplayInfo] = useState({
    currentMonth: '',
    targetMonth: '',
    deadlineDate: null
  });
  const { user } = useAuth();

  // Function to fetch user statuses
  const fetchUserStatuses = async () => {
    try {
      // Don't set loading to true on refreshes to avoid UI flickering
      if (userStatuses.length === 0) {
        setLoading(true);
      }
      
      // Get expense report compliance data (includes deadline info)
      // Add timestamp to prevent browser caching
      const timestamp = new Date().getTime();
      const complianceData = await apiService.getExpenseReportCompliance();
      
      if (!complianceData) {
        throw new Error('Failed to fetch expense report compliance data');
      }
      
      // Get all users to display their status
      const users = await apiService.getUsersByBranch(user.branch);
      
      // Get current and previous month info
      const now = new Date();
      const currentMonth = now.getMonth() + 1; // 0-indexed to 1-indexed
      const currentYear = now.getFullYear();
      
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      // Previous month (target month for expense reports)
      const targetMonth = complianceData.target_month;
      const targetYear = complianceData.target_year;
      
      // Format deadline date if available
      let deadlineDate = null;
      if (complianceData.deadline_date) {
        deadlineDate = new Date(complianceData.deadline_date);
      }
      
      setDisplayInfo({
        currentMonth: `${monthNames[currentMonth - 1]} ${currentYear}`,
        targetMonth: targetMonth ? `${monthNames[targetMonth - 1]} ${targetYear}` : '',
        deadlineDate
      });
      
      // Get expense reports for target month and year with timestamp to prevent caching
      const expenseReports = await apiService.getExpenseReportsByMonth(targetMonth, targetYear);
      
      // Map users to their submission status
      const statusData = users.map(u => {
        const userReport = expenseReports.find(r => r.user_id === u.id);
        
        // More robust check for submission status
        const hasSubmitted = userReport && (
          userReport.is_submitted === true || // Boolean field
          userReport.status === 'submitted' || // Status field
          (userReport.workflow_status && ['submitted', 'review', 'gl_coded', 'final', 'sent_to_billing'].includes(userReport.workflow_status.toLowerCase()))
        );
        
        return {
          id: u.id,
          name: u.username || u.name || u.email.split('@')[0],
          email: u.email,
          status: hasSubmitted ? 'Submitted' : 'Not Submitted',
          // Store raw report data for debugging if needed
          report: userReport
        };
      });
      
      setUserStatuses(statusData);
    } catch (err) {
      console.error('Error fetching expense report statuses:', err);
      setError('Failed to load expense report statuses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchUserStatuses();
    
    // Set up polling to refresh data every 8 seconds
    const refreshInterval = setInterval(() => {
      fetchUserStatuses();
    }, 8000); // More frequent updates to catch status changes quickly
    
    // Clean up interval on component unmount
    return () => clearInterval(refreshInterval);
  }, [user]);

  if (loading && userStatuses.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Format deadline display
  let deadlineDisplay = '';
  if (displayInfo.deadlineDate) {
    deadlineDisplay = displayInfo.deadlineDate.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric'
    });
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Expense Report Status - {displayInfo.targetMonth} Reports
      </Typography>
      
      {deadlineDisplay && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {displayInfo.targetMonth} expense reports are due by {deadlineDisplay}, the end of the first full workweek of {displayInfo.currentMonth}.
        </Alert>
      )}
      
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userStatuses.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell align="right">
                  <Chip 
                    label={user.status} 
                    color={user.status === 'Submitted' ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
            {loading && userStatuses.length > 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ExpenseReportStatusSection; 